<template>
  <section>
    <h2 class="mb-0 d-none d-lg-block">{{ $t('designText.title') }}</h2>
    <ValidationObserver ref="p_personalize_form" v-slot="{ validated }" slim>
      <div class="d-flex mt-3">
        <dp-input
          v-model.trim="text1"
          label="Text for line 1"
          :label-class="'d-none d-lg-block'"
          name="Text for Line 1"
          type="text"
          placeholder="Enter first line of text"
          aria-label="Enter first line of text"
          :validation-rules="{ before: { valid: textBeforeValid }, required: true, noEmojis: true, max: 20, is_not: 'Text Line 1' }"
          vid="designTextLine1"
          :validation-state="validated ? 'valid' : null"
          class="design-text-field w-100"
          :class="text1 === 'Text Line 1' ? 'text-dark' : ''"
          tabindex="0"
          @focus="onFieldFocus"
        >
          <template #sub-text>
            <span v-show="text1.length <= 20" class="text-dark">
              {{ 20 - (text1 !== 'Text Line 1' ? text1.length : 0) }}
              {{ 20 - (text1 !== 'Text Line 1' ? text1.length : 0) === 1 ? 'character' : 'characters' }} {{ $t('dp.generic.remaining') }}.
            </span>
          </template>
        </dp-input>
        <!-- Text Sizing -->
        <div class="d-flex flex-column mx-lg-3">
          <div class="mt-n2 mt-lg-0 mx-auto mb-0 d-none d-lg-inline-flex">{{ $t('dp.generic.size') }}</div>
          <b-button
            title="Increase Font Size"
            class="p-0 p-lg-2 mt-lg-2 border-top-left-radius-0 border-bottom-left-radius-0 touch-action-manipulation"
            variant="primary"
            size="sm"
            block
            aria-label="Increase Font Size for line 1"
            @click="increaseFontSize(1)"
          >
            <h2 class="text-white font-weight-bold my-0 px-1">+</h2>
          </b-button>
          <b-button
            title="Reduce Font Size"
            class="mt-n1 mt-lg-2 p-0 p-lg-2 border-top-left-radius-0 border-bottom-left-radius-0 touch-action-manipulation"
            variant="primary"
            size="sm"
            block
            aria-label="Reduce Font Size for line 1"
            @click="reduceFontSize(1)"
          >
            <h2 class="text-white font-weight-bold my-0 px-1">-</h2>
          </b-button>
        </div>
        <!-- Text Positioning -->
        <div class="ml-1 d-flex flex-column">
          <div class="d-none d-lg-inline-flex mx-auto">{{ $t('dp.generic.position') }}</div>
          <div class="d-flex align-items-center mt-lg-2">
            <b-button
              title="Move Left"
              variant="primary"
              class="px-1 py-0 py-lg-1 px-lg-2 design-text-move-caret-left touch-action-manipulation"
              aria-label="Move Left for line 1"
              @click="moveLeft(1)"
            >
              <fa-icon :icon="['fas', 'caret-left']" size="lg" class="d-block d-lg-none" />
              <fa-icon :icon="['fas', 'caret-left']" size="2x" class="d-none d-lg-block" />
            </b-button>
            <div class="justify-content-center d-flex flex-column">
              <b-button
                title="Move Up"
                variant="primary"
                class="py-0 pb-1 px-1 pb-lg-3 px-lg-2 design-text-move-caret-up touch-action-manipulation"
                aria-label="Move Up for line 1"
                @click="moveUp(1)"
              >
                <fa-icon :icon="['fas', 'caret-up']" size="lg" class="d-block d-lg-none" />
                <fa-icon :icon="['fas', 'caret-up']" size="2x" class="d-none d-lg-block" />
              </b-button>
              <b-button
                title="Move Down"
                variant="primary"
                class="py-0 pt-1 px-1 pt-lg-3 px-lg-2 design-text-move-caret-down touch-action-manipulation"
                aria-label="Move Down for line 1"
                @click="moveDown(1)"
              >
                <fa-icon :icon="['fas', 'caret-down']" size="lg" class="d-block d-lg-none" />
                <fa-icon :icon="['fas', 'caret-down']" size="2x" class="d-none d-lg-block" />
              </b-button>
            </div>
            <b-button
              title="Move Right"
              variant="primary"
              class="px-1 py-0 py-lg-1 px-lg-2 design-text-move-caret-right touch-action-manipulation"
              aria-label="Move Right for line 1"
              @click="moveRight(1)"
            >
              <fa-icon :icon="['fas', 'caret-right']" size="lg" class="d-block d-lg-none" />
              <fa-icon :icon="['fas', 'caret-right']" size="2x" class="d-none d-lg-block" />
            </b-button>
          </div>
        </div>
      </div>
      <div v-if="showText2" class="d-flex mb-n3 mt-0 mt-lg-3">
        <dp-input
          v-model.trim="text2"
          label="Text for line 2"
          :label-class="'d-none d-lg-block'"
          name="Text for Line 2"
          type="text"
          placeholder="Optional: Enter second line of text"
          aria-label="Optional: Enter second line of text"
          :validation-rules="{ before: { valid: textBeforeValid }, required: false, noEmojis: true, max: 20, is_not: 'Optional Line 2' }"
          vid="designTextLine2"
          :validation-state="validated ? '' : null"
          class="design-text-field w-100"
          :class="text2 === 'Optional Line 2' ? 'text-dark' : ''"
          tabindex="0"
          @focus="onFieldFocus"
        >
          <template #sub-text>
            <span v-show="text2.length >= 1 && text2.length <= 20 && text2 !== 'Optional Line 2'" class="text-dark">
              {{ 20 - text2.length }} {{ 20 - text2.length === 1 ? 'character' : 'characters' }} {{ $t('dp.generic.remaining') }}.
            </span>
          </template>
        </dp-input>
        <!-- Text Sizing -->
        <div class="d-flex flex-column mx-lg-3">
          <div class="mt-n2 mt-lg-0 mx-auto mb-0 d-none d-lg-inline-flex">Size</div>
          <b-button
            title="Increase Font Size"
            class="p-0 p-lg-2 mt-lg-2 border-top-left-radius-0 border-bottom-left-radius-0 touch-action-manipulation"
            variant="primary"
            size="sm"
            block
            aria-label="Increase Font Size for line 2"
            @click="increaseFontSize(2)"
          >
            <h2 class="text-white font-weight-bold my-0 px-1">+</h2>
          </b-button>
          <b-button
            title="Reduce Font Size"
            class="mt-n1 mt-lg-2 p-0 p-lg-2 border-top-left-radius-0 border-bottom-left-radius-0 touch-action-manipulation"
            variant="primary"
            size="sm"
            block
            aria-label="Reduce Font Size for line 2"
            @click="reduceFontSize(2)"
          >
            <h2 class="text-white font-weight-bold my-0 px-1">-</h2>
          </b-button>
        </div>
        <!-- Text Positioning -->
        <div class="ml-1 d-flex flex-column">
          <div class="d-none d-lg-inline-flex mx-auto">Position</div>
          <div class="d-flex align-items-center mt-lg-2">
            <b-button
              title="Move Left"
              variant="primary"
              class="px-1 py-0 py-lg-1 px-lg-2 design-text-move-caret-left touch-action-manipulation"
              aria-label="Move Left for line 2"
              @click="moveLeft(2)"
            >
              <fa-icon :icon="['fas', 'caret-left']" size="lg" class="d-block d-lg-none" />
              <fa-icon :icon="['fas', 'caret-left']" size="2x" class="d-none d-lg-block" />
            </b-button>
            <div class="justify-content-center d-flex flex-column">
              <b-button
                title="Move Up"
                variant="primary"
                class="py-0 pb-1 px-1 pb-lg-3 px-lg-2 design-text-move-caret-up touch-action-manipulation"
                aria-label="Move Up for line 2"
                @click="moveUp(2)"
              >
                <fa-icon :icon="['fas', 'caret-up']" size="lg" class="d-block d-lg-none" />
                <fa-icon :icon="['fas', 'caret-up']" size="2x" class="d-none d-lg-block" />
              </b-button>
              <b-button
                title="Move Down"
                variant="primary"
                class="py-0 pt-1 px-1 pt-lg-3 px-lg-2 design-text-move-caret-down touch-action-manipulation"
                aria-label="Move Down for line 2"
                @click="moveDown(2)"
              >
                <fa-icon :icon="['fas', 'caret-down']" size="lg" class="d-block d-lg-none" />
                <fa-icon :icon="['fas', 'caret-down']" size="2x" class="d-none d-lg-block" />
              </b-button>
            </div>
            <b-button
              title="Move Right"
              variant="primary"
              class="px-1 py-0 py-lg-1 px-lg-2 design-text-move-caret-right touch-action-manipulation"
              aria-label="Move right for line 2"
              @click="moveRight(2)"
            >
              <fa-icon :icon="['fas', 'caret-right']" size="lg" class="d-block d-lg-none" />
              <fa-icon :icon="['fas', 'caret-right']" size="2x" class="d-none d-lg-block" />
            </b-button>
          </div>
        </div>
      </div>
      <div v-if="showText3">
        <dp-input
          v-model.trim="text3"
          label="Text for line 3"
          name="Text for Line 3"
          type="text"
          placeholder="Optional: Enter third line of text"
          aria-label="Optional: Enter third line of text"
          :validation-rules="{ required: false, max: 20 }"
          vid="designTextLine3"
          :validation-state="validated ? '' : null"
        />
      </div>
    </ValidationObserver>
  </section>
</template>

<script>
import DpInput from './library/Input';
export default {
  name: 'DesignText',
  components: { DpInput },
  props: {
    value: {
      type: Object,
      required: false,
      default: null,
    },
    textBeforeValid: {
      type: Boolean,
      required: false,
      default: true,
    },
    showText2: {
      type: Boolean,
      default: true,
      required: false,
    },
    showText3: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      text1: this.value.text1 || '',
      text2: this.value.text2 || '',
      text3: this.value.text3 || '',
    };
  },
  watch: {
    text1: function () {
      this.$emit('input', { text1: this.text1.substr(0, 20), text2: this.text2.substr(0, 20), text3: this.text3.substr(0, 20) });
      this.$emit('change');
    },
    text2: function () {
      this.$emit('input', { text1: this.text1.substr(0, 20), text2: this.text2.substr(0, 20), text3: this.text3.substr(0, 20) });
      this.$emit('change');
    },
    text3: function () {
      this.$emit('input', { text1: this.text1.substr(0, 20), text2: this.text2.substr(0, 20), text3: this.text3.substr(0, 20) });
      this.$emit('change');
    },
  },
  methods: {
    reduceFontSize(lineNo) {
      return this.$emit('reduceFontSize', lineNo);
    },
    increaseFontSize(lineNo) {
      return this.$emit('increaseFontSize', lineNo);
    },
    moveUp(lineNo) {
      return this.$emit('moveUp', lineNo);
    },
    moveDown(lineNo) {
      return this.$emit('moveDown', lineNo);
    },
    moveLeft(lineNo) {
      return this.$emit('moveLeft', lineNo);
    },
    moveRight(lineNo) {
      return this.$emit('moveRight', lineNo);
    },
    onFieldFocus() {
      if (this.text1 === 'Text Line 1') this.text1 = '';
      if (this.text2 === 'Optional Line 2') this.text2 = '';
    },
  },
};
</script>

<style lang="scss">
.design-text-field .form-group .form-control {
  height: 3.5rem;
  @media (min-width: 992px) {
    height: 4rem;
    font-size: 1.5rem;
  }
}

.design-text-field.text-dark .form-group .form-control {
  color: grey;
}

@media (min-width: 0px) and (max-width: 991px) {
  .design-text-field .form-group .form-control {
    border-right: none;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
  .border-top-left-radius-0 {
    border-top-left-radius: 0;
  }

  .border-bottom-left-radius-0 {
    border-bottom-left-radius: 0;
  }

  .border-top-right-radius-0 {
    border-top-right-radius: 0;
  }

  .border-bottom-right-radius-0 {
    border-bottom-right-radius: 0;
  }
}

.design-text-move-caret-left,
.design-text-move-caret-up,
.design-text-move-caret-right,
.design-text-move-caret-down {
  border-radius: 0;
}

.design-text-move-caret-left {
  border-top-left-radius: 0.25rem;
  border-bottom-left-radius: 0.25rem;
}

.design-text-move-caret-up {
  border-top-left-radius: 0.25rem;
  border-top-right-radius: 0.25rem;
}

.design-text-move-caret-right {
  border-top-right-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
}

.design-text-move-caret-down {
  border-bottom-left-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
}

.touch-action-manipulation {
  touch-action: manipulation;
  -ms-touch-action: manipulation;
}
</style>
